/**
 * Types mirrored from `plan/smart-capture/INTERFACES.md` sections 1 and 3,
 * with the section 15 additions (15.1 to 15.5) mirrored member for member
 * from `apps/capture-api/src/shared/types.ts` and `ingestion/types.ts`.
 * `apps/capture-api/src/contracts/` compares the two by member name and
 * optionality, so a drift here fails the capture-api test run.
 * This file is a client-side copy, not a generated artefact: the contract
 * is frozen there, and this app does not import across the monorepo
 * boundary into `apps/capture-api`. Keep these in lockstep with the
 * interface file by hand; do not add fields the contract does not name.
 *
 * Two shapes the contract references but never defines in section 3
 * (`Page<T>` and `PartnerCaptureManifest`) are filled in here as a working
 * assumption. See the SC3 packet report for the exact gap; this is a
 * client-side fill-in, not a redefinition of a frozen type.
 *
 * The Capture Job shapes and live transcription mirror
 * `plan/smart-capture/CAPTURE_JOBS.md` sections 1 to 3, member for member
 * from the same two capture-api files.
 */

export type CaptureMode = 'photo' | 'burst' | 'document' | 'voice' | 'barcode' | 'manual';

/**
 * The job the capture was taken for (INTERFACES.md section 1). Optional on
 * the wire: a capture with none extracts against the item standard alone.
 * `lib/capture-workflows.ts` carries the operator-facing labels.
 */
export type CaptureWorkflow =
  | 'mdm_quality'
  | 'procurement_request'
  | 'mro_spare_parts'
  | 'quality_inspection'
  | 'compliance_audit'
  | 'inventory_recon';

export type CaptureStatus =
  | 'QUEUED'
  | 'RECEIVED'
  | 'PREFLIGHT'
  | 'EXTRACTING'
  | 'VALIDATING'
  | 'SCORED'
  | 'AUTO_APPROVED'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'RECAPTURE_REQUESTED'
  | 'STAGED'
  | 'SUBMITTED'
  | 'QUARANTINED'
  | 'REMOVED';

export type PreflightGate = 'FINGERPRINT_CACHE' | 'IDENTITY_LOOKUP' | 'FAILURE_PATTERN';

export type LineageStage =
  | 'RAW_EVIDENCE'
  | 'DETERMINISTIC_READ'
  | 'MODEL_EXTRACTION'
  | 'CROSS_VERIFICATION'
  | 'TAXONOMY_VALIDATION'
  | 'QUALITY_GATE'
  | 'VERIFIED';

export interface EvidenceRef {
  kind: 'image_region' | 'ocr_span' | 'barcode' | 'voice_span' | 'pdd_default' | 'brain_match';
  assetKey?: string;
  detail: Record<string, unknown>;
}

export interface FieldValue {
  value: string | number | boolean | null;
  stage: LineageStage;
  confidence?: number;
  verified: boolean;
  verifiedBy?: string;
  evidence: EvidenceRef[];
  /** What the machine read, set once on the first human edit and never
   *  overwritten (INTERFACES.md 15.1). */
  machineValue?: { value: string | number | boolean | null; stage: LineageStage; confidence?: number };
}

export interface CaptureConfidence {
  perField: Record<string, number>;
  fused: number;
  calibrationVersion: string;
}

export type ReviewLane = 'OPERATOR_RESOLVED' | 'QA_SAMPLE' | 'EXCEPTION' | 'MANAGER';

export type ReasonCode =
  | 'MISSING_REQUIRED'
  | 'VALIDATOR_FAILED'
  | 'QUALITY_GATE'
  | 'LOW_SCORE'
  | 'NOT_CAPTURER'
  | 'OPERATOR_ESCALATED'
  | 'PLACEHOLDER_CALIBRATION';

export type CorrectionReason = 'misread' | 'wrong_field' | 'missing' | 'unit' | 'not_on_item' | 'other';

export interface FieldCorrection {
  field: string;
  before: FieldValue['value'];
  after: FieldValue['value'];
  reason?: CorrectionReason;
  confidenceAtPresentation?: number;
}

export interface ReviewEvent {
  at: string;
  actor: string;
  /** `confirm` is the operator's sign-off, `escalate` "Send to manager with a question". */
  verdict:
    | 'approve'
    | 'reject'
    | 'request_recapture'
    | 'edit'
    | 'confirm'
    | 'escalate'
    | 'remove'
    | 'restore';
  fieldsEdited?: string[];
  note?: string;
  lane?: ReviewLane;
  corrections?: FieldCorrection[];
}

export type VoiceNoteStatus = 'UPLOADED' | 'TRANSCRIBING' | 'TRANSCRIBED' | 'FAILED';

export type VoiceNoteContentType = 'audio/webm' | 'audio/mp4' | 'audio/ogg' | 'audio/wav';

/** 'NOTE' is a note for the reviewer. 'CONTEXT' is a hint for Rerun AI. */
export type VoiceNotePurpose = 'NOTE' | 'CONTEXT';

export type VoiceNoteFailureReason = 'PROVIDER_UNAVAILABLE' | 'UNSUPPORTED_AUDIO' | 'EMPTY_AUDIO' | 'UNKNOWN';

/** Maximum clip length, INTERFACES.md 15.3. */
export const MAX_VOICE_NOTE_DURATION_MS = 120_000;

/** Maximum voice notes on one capture, INTERFACES.md 15.3. */
export const MAX_VOICE_NOTES_PER_CAPTURE = 5;

export interface VoiceNote {
  noteId: string;
  /** Held apart from assetKeys, never counted as a frame. Absent on a note
   *  saved from live transcription, which carries only its transcript. */
  audioKey?: string;
  contentType?: VoiceNoteContentType;
  durationMs: number;
  languageCode?: string;
  status: VoiceNoteStatus;
  transcript?: string;
  /** Mean item confidence from Amazon Transcribe, 0 to 1. */
  transcriptConfidence?: number;
  failureReason?: VoiceNoteFailureReason;
  purpose: VoiceNotePurpose;
  recordedBy: string;
  recordedAt: string;
  transcribedAt?: string;
}

export type RerunTrigger = 'RERUN' | 'RETAKE' | 'ADD_FRAME';

export interface RerunIteration {
  iteration: number;
  requestedBy: string;
  requestedAt: string;
  /** The fields re-analysed. Empty means all attributes. */
  fields: string[];
  context?: string;
  trigger: RerunTrigger;
  /** Field values before this iteration, for the history view. */
  before: Record<string, FieldValue['value']>;
  completedAt?: string;
}

export type DispatchProduct = 'CLASSIFY' | 'CLASSIFY_AND_ENRICH';

export interface CaptureDispatch {
  product: DispatchProduct;
  status: 'DISPATCHED' | 'FAILED';
  /** The account API job id. */
  jobId?: string;
  reason?: string;
  dispatchedBy: string;
  at: string;
}

/** Where the capture came from. Absent on a record means PWA. */
export type CaptureChannel = 'PWA' | 'DESKTOP';

/** PK `TENANT#<tenantId>`, SK `CAPTURE#<captureId>`. */
/** Reject reasons, mirrored from capture-api `REJECT_REASONS` (plan/portal-final/INTERFACES.md section 3). */
export type RejectReasonCode = 'UNREADABLE' | 'NO_ITEM' | 'DUPLICATE' | 'WRONG_ITEM' | 'INCOMPLETE' | 'OTHER';

export const REJECT_REASON_LABELS: Record<RejectReasonCode, string> = {
  UNREADABLE: 'Unreadable',
  NO_ITEM: 'No item',
  DUPLICATE: 'Duplicate',
  WRONG_ITEM: 'Wrong item',
  INCOMPLETE: 'Incomplete',
  OTHER: 'Other',
};

/** Present on a REJECTED capture. */
export interface CaptureRejection {
  code: RejectReasonCode;
  detail?: string;
  auto: boolean;
  by: string;
  at: string;
  duplicateOf?: string;
}

export interface CaptureRecord {
  PK: string;
  SK: string;
  tenantId: string;
  captureId: string;
  /** The Capture Job the capture belongs to (CAPTURE_JOBS.md section 1).
   *  Absent only on captures taken before jobs existed. */
  captureJobId?: string;
  /** Kept for compatibility. New code reads `captureJobId`. */
  workItemId?: string;
  /** Kept for compatibility. New code reads `captureJobId`. */
  sessionId: string;
  siteId?: string;
  teamId?: string;
  capturedBy: string;
  mode: CaptureMode;
  /** The job the operator picked. Absent on captures taken before it shipped. */
  workflow?: CaptureWorkflow;
  status: CaptureStatus;
  resolvedByGate: PreflightGate | null;
  fingerprint: string;
  assetKeys: string[];
  voiceTranscript?: string;
  fields: Record<string, FieldValue>;
  confidence?: CaptureConfidence;
  reviewTrail: ReviewEvent[];
  creditsConsumed: number;
  revenueUsdMinorAtConsumption: number;
  ttl: number;
  createdAt: string;
  updatedAt: string;
  // INTERFACES.md 15.1 additions, all optional on the wire.
  voiceNotes?: VoiceNote[];
  iterations?: RerunIteration[];
  lane?: ReviewLane;
  reasonCode?: ReasonCode;
  dispatch?: CaptureDispatch;
  channel?: CaptureChannel;
  /** The Atlas job ids this capture was sent in. Absent or empty means never sent. */
  sentInAtlasJobs?: string[];
  projectId?: string;
  rejection?: CaptureRejection;
}

export type CaptureJobStatus = 'OPEN' | 'CLOSED';

/** A Capture Job (CAPTURE_JOBS.md section 1). Every capture belongs to one. */
export interface CaptureJob {
  jobId: string;
  name: string;
  /** One multi-line field for description, location and special instructions. */
  instructions: string;
  /** Member emails of the operators assigned to the job. */
  operatorEmails: string[];
  status: CaptureJobStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Derived per request, never stored. */
  counts?: { total: number; inReview: number; approved: number; notYetSent: number; removed?: number };
  submissions: AtlasSubmission[];
  /** Files for the operator, oldest first, at most 20 (CAPTURE_JOBS.md section 8.1). Never carries the S3 key. */
  attachments: JobAttachment[];
  /** Set when the job was created from an Atlas job's below-quality rows. */
  origin?: {
    type: 'atlas';
    atlasJobId: string;
    atlasFileName: string;
    /** The Atlas job's jobType. */
    atlasJobType: string;
    rowCount: number;
  };
  /** The platform project the job belongs to. Absent means No project. */
  projectId?: string;
  projectName?: string;
  /** Resolved shot list: the job's own, else the item standard's default. At most 3 labels. */
  shotList?: string[];
}

/** One file attached to a Capture Job (CAPTURE_JOBS.md section 8.1). */
export interface JobAttachment {
  attachmentId: string;
  fileName: string;
  contentType: string;
  byteLength: number;
  uploadedBy: string;
  uploadedAt: string;
}

/** A listed attachment with its 15-minute presigned GET (`GET /capture-jobs/{id}/attachments`). */
export interface JobAttachmentLink extends JobAttachment {
  downloadUrl: string;
  expiresAt: string;
}

export interface ListJobAttachmentsResponse {
  items: JobAttachmentLink[];
}

/** One hand-off of a Capture Job's captures to an Atlas job. */
export interface AtlasSubmission {
  atlasJobId: string;
  fileName: string;
  captureIds: string[];
  submittedBy: string;
  submittedAt: string;
}

/** `GET /capture-jobs` query. An operator sees only the OPEN jobs assigned to them. */
export interface ListCaptureJobsQuery {
  status?: CaptureJobStatus;
  /** A project id, or `none`. */
  projectId?: string;
}

/** `GET /capture-jobs/{id}/captures` query. */
export interface ListJobCapturesQuery {
  status?: CaptureStatus;
  cursor?: string;
}

/** One row of `GET /capture-jobs/{id}/captures`. */
export interface CaptureQueueRow {
  captureId: string;
  /** One line naming the item, derived from the read fields. */
  headline: string;
  status: CaptureStatus;
  /** Absent until the capture has been scored into a lane. */
  lane?: ReviewLane;
  /** The first frame's asset key. Absent on a capture with no image. */
  thumbnailKey?: string;
  /** The capturer's display name. */
  capturedBy: string;
  createdAt: string;
  /** The Atlas job ids this capture was sent in. Empty means never sent. */
  sentInAtlasJobs: string[];
  /** Present on a REMOVED capture only (CAPTURE_JOBS.md section 9). */
  removal?: {
    removedBy: string;
    removedAt: string;
    removedFromStatus: CaptureStatus;
    reason?: string;
  };
  /** Present on a REJECTED capture. */
  rejection?: CaptureRejection;
}

/** `POST /capture/transcribe-session` (CAPTURE_JOBS.md section 3). */
export interface TranscribeSessionRequest {
  languageCode?: string;
}

/** A presigned Amazon Transcribe streaming WebSocket URL, valid for 5
 *  minutes, for PCM 16 kHz mono audio. */
export interface TranscribeSessionResponse {
  url: string;
  expiresAt: string;
}

/** Derived per request from the item standard and the record, never stored. */
export interface CaptureAttention {
  /** Every attribute of the capture's item standard, read or empty. */
  attributes: { name: string; required: boolean; state: 'READ' | 'EMPTY' | 'VERIFIED' }[];
  missingRequired: string[];
  lowConfidence: string[];
}

export type QualityGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export type QualityCardPartName =
  | 'Identity'
  | 'Technical attributes'
  | 'Descriptions'
  | 'Completeness'
  | 'Validation'
  | 'Model confidence'
  | 'Consistency';

/** Display only. Gates nothing. */
export interface QualityCard {
  /** 0 to 100. */
  overall: number;
  grade: QualityGrade;
  /** One sentence naming the fix. */
  verdict: string;
  parts: { name: QualityCardPartName; weight: number; score: number; note?: string }[];
}

/**
 * `GET /capture/{id}` (INTERFACES.md 15.2). `attention` and `qualityCard`
 * are optional on the wire: an API deployed before the approval work
 * returns the bare record, and the review screen has to render it.
 */
export type CaptureDetailResponse = CaptureRecord & {
  attention?: CaptureAttention;
  qualityCard?: QualityCard;
};

/** QualityGateResult, INTERFACES.md section 3. Advisory in the client,
 * always sent to the server (open decision B1 resolves server-side). */
export interface QualityGateResult {
  passed: boolean;
  checks: { name: string; passed: boolean; score: number }[];
}

export interface CaptureSubmission {
  /** The Capture Job the capture belongs to. `POST /capture` answers 400
   *  without it, 404 for an unknown job and 409 for a CLOSED one. */
  captureJobId: string;
  mode: CaptureMode;
  workflow?: CaptureWorkflow;
  fingerprint: string;
  assetKeys: string[];
  /** Kept for compatibility. New code reads `captureJobId`. */
  sessionId: string;
  /** Kept for compatibility. New code reads `captureJobId`. */
  workItemId?: string;
  siteId?: string;
  teamId?: string;
  voiceTranscript?: string;
  clientQuality?: QualityGateResult;
  manualFields?: Record<string, string>;
  /** INTERFACES.md 15.6. Absent means PWA. */
  channel?: CaptureChannel;
}

export interface UploadUrlRequest {
  mode: CaptureMode;
  fingerprint: string;
  contentType: string;
  byteLength: number;
  /** The Capture Job the bytes are for. The API answers 400 without it (CAPTURE_JOBS.md section 1). */
  captureJobId: string;
}

export interface UploadUrlResponse {
  assetKey: string;
  uploadUrl: string;
  expiresAt: string;
  /** Set when the tenant already has this fingerprint. The client skips
   * the upload entirely: the bytes never leave the device a second time. */
  duplicateOf?: string;
}

export interface SubmitCaptureResponse {
  captureId: string;
  status: CaptureStatus;
  resolvedByGate: PreflightGate | null;
}

/**
 * A short-lived read URL for one stored asset, minted per request by
 * `GET /capture/{id}/assets`. Mirrors `PresignedDownload` in
 * apps/capture-api/src/ingestion/asset-upload.ts. The raw bucket stays
 * private, so a URL carries the caller's authority and expires.
 */
export interface PresignedDownload {
  assetKey: string;
  url: string;
  expiresAt: string;
}

/** `GET /capture/{id}/assets`, in capture order. */
export interface CaptureAssetsResponse {
  captureId: string;
  assets: PresignedDownload[];
}

export interface RecaptureResponse {
  workItemId: string;
  captureId: string;
}

/** `POST /capture/{id}/confirm` (INTERFACES.md 15.2). An empty body agrees
 * with every value. */
export interface ConfirmCaptureRequest {
  fieldEdits?: Record<string, string | number | boolean | null>;
  /** Values the extraction did not read. Names must be item-standard attributes. */
  fieldAdds?: Record<string, string | number | boolean | null>;
  reasons?: Record<string, CorrectionReason>;
  note?: string;
}

/**
 * The members after `editedFields` are optional on the wire: the confirm
 * handler deployed before the approval work returns the first three alone.
 */
export interface ConfirmCaptureResponse {
  captureId: string;
  confirmedFields: string[];
  editedFields: string[];
  addedFields?: string[];
  /** APPROVED when the ADR-004 rule held, otherwise IN_REVIEW. */
  status?: CaptureStatus;
  lane?: ReviewLane;
  reasonCode?: ReasonCode;
  /** Fields still needing attention. Empty means resolved on the spot. */
  outstanding?: string[];
}

/** `POST /capture/{id}/frames`, a retake or an added frame. */
export interface FrameChange {
  action: 'REPLACE' | 'ADD';
  /** Zero-based index of the frame replaced. Required for REPLACE. */
  index?: number;
  /** From POST /capture/upload-url. */
  assetKey: string;
  fingerprint: string;
}

/** `POST /capture/{id}/rerun`. */
export interface RerunRequest {
  /** Fields to re-analyse. Empty re-analyses every attribute. */
  fields: string[];
  context?: string;
  /** Include the transcripts of CONTEXT voice notes. Default true. */
  includeVoiceContext?: boolean;
}

/** The 202 body of the frames and rerun routes. */
export interface ReExtractionAccepted {
  captureId: string;
  status: 'EXTRACTING';
  iteration: number;
}

export interface EscalateRequest {
  question: string;
}

export interface EscalateResponse {
  captureId: string;
  status: 'IN_REVIEW';
  reasonCode: 'OPERATOR_ESCALATED';
}

/** `POST /capture/{id}/voice-notes/upload-url` (INTERFACES.md 15.3). */
export interface VoiceNoteUploadUrlRequest {
  contentType: VoiceNoteContentType;
  byteLength: number;
  durationMs: number;
  purpose: VoiceNotePurpose;
}

export interface VoiceNoteUploadUrlResponse {
  noteId: string;
  audioKey: string;
  uploadUrl: string;
  expiresAt: string;
}

/** `POST /capture/{id}/voice-notes`. A recorded clip sends `audioKey`, a
 *  live transcription sends `transcript`; one of the two is always present. */
export interface CreateVoiceNoteRequest {
  noteId: string;
  /** Optional when `transcript` is supplied. */
  audioKey?: string;
  /** The final text of a live transcription, as the operator left it. */
  transcript?: string;
  durationMs: number;
  purpose: VoiceNotePurpose;
  languageCode?: string;
}

export interface CreateVoiceNoteResponse {
  noteId: string;
  status: 'TRANSCRIBING';
}

/** `GET /capture/settings` (INTERFACES.md 15.5). The operator reads it and
 * never writes it: the platform user owns the choice. */
export interface SmartCaptureSettings {
  /** 0 to 1. Default 0.05. */
  qaSampleRate: number;
  updatedBy?: string;
  updatedAt?: string;
  /** 0 to 120. */
  undoWindowSeconds?: number;
  autoReject?: { unreadable: boolean; noItem: boolean; duplicate: boolean };
}

/** `POST /review/captures/{id}/verdict`, INTERFACES.md section 12. The
 * reviewer's footer on the shared review route calls it (ADR-006). */
export interface VerdictRequest {
  verdict: 'approve' | 'reject' | 'request_recapture' | 'edit';
  fieldEdits?: Record<string, string | number | boolean | null>;
  reason?: string;
  note?: string;
}

export interface VerdictResponse {
  captureId: string;
  status: CaptureStatus;
  workItemId?: string;
}

/** Generic cursor page. Not spelled out in INTERFACES.md section 3; every
 * route there that returns `Page<T>` uses this shape. */
export interface Page<T> {
  items: T[];
  cursor: string | null;
}

export interface ListCapturesQuery {
  status?: CaptureStatus;
  sessionId?: string;
  cursor?: string;
}

/** PartnerCaptureManifest is referenced by `GET /capture/manifest` in
 * INTERFACES.md section 3 but never defined there. This is the client's
 * working shape: partner identity, brand theme tokens, and which capture
 * modes and item types the tenant has enabled, consistent with the
 * existing `libs/partner-configs` whitelabel convention this platform
 * already runs. */
export interface PartnerCaptureManifest {
  partnerId: string;
  displayName: string;
  theme: {
    brandColor: string;
    accentColor?: string;
    surfaceColor?: string;
    logoUrl?: string;
  };
  enabledModes: CaptureMode[];
  itemTypes?: string[];
  /** Server-side quality gate posture for this tenant. The client's own
   * gate result is always sent regardless of this value (see quality-gate.ts). */
  qualityGateEnforcement: 'advisory' | 'blocking';
}

/**
 * `GET /capture/me` (OPERATOR_ACCESS.md section 3), mirrored member for
 * member from `apps/capture-api/src/access/types.ts`. When a permission is
 * added there, add it here in the same change: the app reads an absent key
 * as refused.
 */
export type UserPermission =
  | 'classify'
  | 'enrich'
  | 'descriptions'
  | 'requestQuote'
  | 'acceptQuote'
  | 'apiAccess'
  /** Use the Smart Capture app to capture items. */
  | 'captureInField'
  /** Review, approve and reject captures (console and shared review screen). */
  | 'reviewCaptures';

export type TeamRole = 'client_admin' | 'client_user' | 'capture_operator';

/** `GET /capture/me` (OPERATOR_ACCESS.md sections 2 and 3). Read once after
 * sign-in and shared app-wide by `AccessProvider`. */
export interface MyAccess {
  role: 'owner' | TeamRole;
  /** Resolved, every key present. */
  permissions: Record<UserPermission, boolean>;
}

/** The platform's shared error envelope, INTERFACES.md section 3. */
export type ApiErrorCode =
  | 'ValidationError'
  | 'Unauthorized'
  | 'Forbidden'
  | 'NotFound'
  | 'Conflict'
  | 'PaymentRequired'
  | 'RateLimited'
  | 'InternalError';

export interface ApiErrorEnvelope {
  error: ApiErrorCode;
  message: string;
}
